import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Chip,
	Paper,
	Divider,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Title } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
	MCPSessionDetail,
	MCPAnalysis,
	getMCPSession,
	analyzeSession,
	getSessionAnalysis,
} from '../../Services/quantiAI';

export const AIConversationDetail = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [session, setSession] = useState<MCPSessionDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [analysis, setAnalysis] = useState<MCPAnalysis | null>(null);
	const [analyzing, setAnalyzing] = useState(false);
	const [analysisError, setAnalysisError] = useState<string | null>(null);

	useEffect(() => {
		const loadSession = async () => {
			if (!id) return;
			setLoading(true);
			setError(null);
			try {
				const data = await getMCPSession(id);
				setSession(data);
				// Charger l'analyse existante si disponible
				try {
					const existingAnalysis = await getSessionAnalysis(id);
					setAnalysis(existingAnalysis);
				} catch {
					// Pas d'analyse existante, c'est normal
				}
			} catch (err: any) {
				setError(err.message || 'Impossible de charger la session');
			} finally {
				setLoading(false);
			}
		};
		loadSession();
	}, [id]);

	const handleAnalyze = async () => {
		if (!id) return;
		setAnalyzing(true);
		setAnalysisError(null);
		try {
			const result = await analyzeSession(id);
			setAnalysis(result);
		} catch (err: any) {
			setAnalysisError(err.message || "Erreur lors de l'analyse");
		} finally {
			setAnalyzing(false);
		}
	};

	const formatDuration = (ms: number | undefined) => {
		if (!ms) return '-';
		if (ms < 1000) return `${ms}ms`;
		if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
		return `${(ms / 60000).toFixed(1)}min`;
	};

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		return date.toLocaleString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	const getRatingIcon = (rating: string) => {
		switch (rating) {
			case 'positive':
				return <ThumbUpIcon color="success" />;
			case 'negative':
				return <ThumbDownIcon color="error" />;
			default:
				return null;
		}
	};

	const getRatingColor = (rating: string): 'success' | 'error' | 'default' => {
		switch (rating) {
			case 'positive':
				return 'success';
			case 'negative':
				return 'error';
			default:
				return 'default';
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Alert severity="error">{error}</Alert>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-conversations')}
						sx={{ mt: 2 }}
					>
						Retour à la liste
					</Button>
				</CardContent>
			</Card>
		);
	}

	if (!session) {
		return (
			<Card>
				<CardContent>
					<Alert severity="warning">Session non trouvée</Alert>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-conversations')}
						sx={{ mt: 2 }}
					>
						Retour à la liste
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
		<Card sx={{ overflow: 'hidden' }}>
			<Title title={`Session MCP - ${session.user_email || session.user_id}`} />
			<CardContent sx={{ overflow: 'hidden', minWidth: 0 }}>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate('/ai-conversations')}
					sx={{ mb: 3 }}
				>
					Retour à la liste
				</Button>

				{/* Session info */}
				<Paper sx={{ p: 2, mb: 3 }}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={3}>
							<Typography variant="body2" color="text.secondary">
								Utilisateur
							</Typography>
							<Typography variant="body1">
								{session.user_email || `User #${session.user_id}`}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<Typography variant="body2" color="text.secondary">
								Projet
							</Typography>
							<Typography variant="body1">
								{session.project_folder_id || '-'}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Typography variant="body2" color="text.secondary">
								Client
							</Typography>
							<Typography variant="body1">{session.client_type || '-'}</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Typography variant="body2" color="text.secondary">
								Messages / Tools
							</Typography>
							<Typography variant="body1">
								{session.message_count} / {session.tool_call_count}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={6} md={2}>
							<Typography variant="body2" color="text.secondary">
								Durée totale
							</Typography>
							<Typography variant="body1">
								{formatDuration(session.total_duration_ms)}
							</Typography>
						</Grid>
					</Grid>
					<Divider sx={{ my: 2 }} />
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" color="text.secondary">
								Début
							</Typography>
							<Typography variant="body1">{formatDate(session.started_at)}</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" color="text.secondary">
								Dernière activité
							</Typography>
							<Typography variant="body1">
								{formatDate(session.last_activity_at)}
							</Typography>
						</Grid>
					</Grid>
				</Paper>

				{/* Analyse LLM */}
				<Paper sx={{ p: 2, mb: 3 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: analysis ? 2 : 0 }}>
						<Typography variant="h6">
							Analyse IA
						</Typography>
						<Button
							variant="contained"
							startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
							onClick={handleAnalyze}
							disabled={analyzing}
							size="small"
						>
							{analyzing ? 'Analyse en cours...' : analysis ? 'Relancer l\'analyse' : 'Analyser la session'}
						</Button>
					</Box>

					{analysisError && (
						<Alert severity="error" sx={{ mt: 1 }}>{analysisError}</Alert>
					)}

					{analysis && analysis.status === 'completed' && analysis.result && (
						<Box>
							{/* Score */}
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
								<Chip
									label={`Score : ${analysis.result.score}/10`}
									color={analysis.result.score >= 7 ? 'success' : analysis.result.score >= 4 ? 'warning' : 'error'}
									size="medium"
								/>
								<Typography variant="body2" color="text.secondary">
									{analysis.model} — {analysis.total_tokens} tokens — {(analysis.duration_ms / 1000).toFixed(1)}s
								</Typography>
							</Box>

							{/* Summary */}
							<Typography variant="body1" sx={{ mb: 2 }}>
								{analysis.result.summary}
							</Typography>

							<Grid container spacing={2}>
								{/* Points positifs */}
								{analysis.result.positive_points?.length > 0 && (
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" color="success.main" gutterBottom>
											Points positifs
										</Typography>
										<ul style={{ margin: 0, paddingLeft: 20 }}>
											{analysis.result.positive_points.map((p, i) => (
												<li key={i}><Typography variant="body2">{p}</Typography></li>
											))}
										</ul>
									</Grid>
								)}

								{/* Améliorations */}
								{analysis.result.improvements?.length > 0 && (
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" color="warning.main" gutterBottom>
											Améliorations
										</Typography>
										<ul style={{ margin: 0, paddingLeft: 20 }}>
											{analysis.result.improvements.map((p, i) => (
												<li key={i}><Typography variant="body2">{p}</Typography></li>
											))}
										</ul>
									</Grid>
								)}

								{/* Recommandations */}
								{analysis.result.recommendations?.length > 0 && (
									<Grid item xs={12}>
										<Typography variant="subtitle2" color="info.main" gutterBottom>
											Recommandations
										</Typography>
										<ul style={{ margin: 0, paddingLeft: 20 }}>
											{analysis.result.recommendations.map((p, i) => (
												<li key={i}><Typography variant="body2">{p}</Typography></li>
											))}
										</ul>
									</Grid>
								)}
							</Grid>

							{/* Erreurs */}
							{analysis.result.errors_summary && (
								<Alert severity="warning" sx={{ mt: 2 }}>
									{analysis.result.errors_summary}
								</Alert>
							)}
						</Box>
					)}

					{analysis && analysis.status === 'failed' && (
						<Alert severity="error" sx={{ mt: 1 }}>
							Analyse échouée : {analysis.error_message}
						</Alert>
					)}
				</Paper>

				{/* Feedback */}
				{session.feedback && session.feedback.length > 0 && (
					<Box sx={{ mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							Feedback ({session.feedback.length})
						</Typography>
						{session.feedback.map((fb) => (
							<Paper key={fb.id} sx={{ p: 2, mb: 1 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									{getRatingIcon(fb.rating)}
									<Chip
										label={fb.rating}
										size="small"
										color={getRatingColor(fb.rating)}
									/>
									{fb.category && (
										<Chip label={fb.category} size="small" variant="outlined" />
									)}
									{fb.tool_name && (
										<Chip
											label={fb.tool_name}
											size="small"
											variant="outlined"
											color="info"
										/>
									)}
									<Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
										{formatDate(fb.created_at)}
									</Typography>
								</Box>
								{fb.original_question && (
									<Typography variant="body2" sx={{ mb: 1 }}>
										<strong>Question:</strong> {fb.original_question}
									</Typography>
								)}
								{fb.comment && (
									<Typography variant="body2">
										<strong>Commentaire:</strong> {fb.comment}
									</Typography>
								)}
							</Paper>
						))}
					</Box>
				)}

				{/* Messages */}
				<Typography variant="h6" gutterBottom>
					Messages ({session.messages?.length || 0})
				</Typography>
				{session.messages && session.messages.length > 0 ? (
					session.messages.map((msg, index) => (
						<Accordion key={msg.id} defaultExpanded={index < 5} sx={{ overflow: 'hidden' }}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										width: '100%',
										pr: 2,
									}}
								>
									{msg.success ? (
										<CheckCircleIcon color="success" fontSize="small" />
									) : (
										<ErrorIcon color="error" fontSize="small" />
									)}
									<Chip
										label={msg.tool_name || msg.method}
										size="small"
										color={msg.tool_name ? 'primary' : 'default'}
									/>
									{msg.duration_ms && (
										<Typography variant="body2" color="text.secondary">
											{formatDuration(msg.duration_ms)}
										</Typography>
									)}
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ ml: 'auto' }}
									>
										{formatDate(msg.created_at)}
									</Typography>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								{msg.tool_arguments && (
									<Box sx={{ mb: 2 }}>
										<Typography variant="subtitle2" gutterBottom>
											Arguments
										</Typography>
										<Paper
											sx={{
												p: 1,
												bgcolor: 'grey.100',
												fontFamily: 'monospace',
												fontSize: '0.75rem',
												overflow: 'auto',
												maxHeight: 200,
											}}
										>
											<pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
												{JSON.stringify(msg.tool_arguments, null, 2)}
											</pre>
										</Paper>
									</Box>
								)}
								{msg.result_text && (
									<Box sx={{ mb: 2 }}>
										<Typography variant="subtitle2" gutterBottom>
											Résultat{' '}
											{msg.result_size && (
												<Typography
													component="span"
													variant="body2"
													color="text.secondary"
												>
													({msg.result_size} bytes)
												</Typography>
											)}
										</Typography>
										<Paper
											sx={{
												p: 1,
												bgcolor: 'grey.100',
												fontFamily: 'monospace',
												fontSize: '0.75rem',
												overflow: 'auto',
												maxHeight: 300,
												whiteSpace: 'pre-wrap',
												wordBreak: 'break-word',
											}}
										>
											{msg.result_text}
										</Paper>
									</Box>
								)}
								{msg.error_message && (
									<Alert severity="error">{msg.error_message}</Alert>
								)}
							</AccordionDetails>
						</Accordion>
					))
				) : (
					<Alert severity="info">Aucun message dans cette session</Alert>
				)}
			</CardContent>
		</Card>
		</Box>
	);
};
